import React from 'react';
import toast, { Toaster, resolveValue } from 'react-hot-toast';
import successIcon from '../assets/success.svg';
import errorIcon from '../assets/error.svg';
import { translate } from '../i18n';
import './CustomToast.css';

/**
 * Custom Toast Component matching the design system
 */
export const CustomToast = ({ t, type, title, message }) => {
    const content = resolveValue(t.message, t);
    const isSuccess = (type === 'success' || type === 'blank') && !content?.isDelete;
    const isError = type === 'error' || type === 'delete' || content?.isDelete;

    return (
        <div
            className={`custom-toast-container ${isSuccess ? 'success' : isError ? 'error' : ''} ${t.visible ? 'animate-enter' : 'animate-leave'
                }`}
            style={{
                ...t.style,
                opacity: t.visible ? 1 : 0,
            }}
            role="alert"
            aria-live="polite"
        >
            <div className="toast-content-wrapper">
                <div className="toast-title">{title}</div>
                <div className="toast-message">{message}</div>
            </div>
            <div className="toast-icon-container">
                <div className="toast-icon-wrapper">
                    {isError ? (
                        <img src={errorIcon} alt={translate('messages.toast.error_title', {}, 'Error!')} style={{ width: '100%', height: '100%' }} />
                    ) : (
                        <img src={successIcon} alt={translate('messages.toast.success_title', {}, 'Success!')} style={{ width: '100%', height: '100%' }} />
                    )}
                </div>
            </div>
        </div>
    );
};

/**
 * Global Renderer for use in App.js <Toaster>
 */
export const ToastRenderer = ({ toast: t }) => {
    const content = resolveValue(t.message, t);

    let displayTitle = '';
    let displayMessage = '';

    const resolveValueText = (value, params, fallback) => {
        if (typeof value !== 'string') return fallback || '';
        if (value.includes('.')) return translate(value, params || {}, fallback || value);
        return value;
    };

    // Standardize headings based on toast type with i18n defaults
    if (t.type === 'success') {
        displayTitle = translate('messages.toast.success_title', {}, 'Success!');
    } else if (t.type === 'delete' || content?.isDelete) {
        displayTitle = translate('messages.toast.success_title', {}, 'Success!');
    } else if (t.type === 'error') {
        displayTitle = translate('messages.toast.error_title', {}, 'Error!');
    } else {
        displayTitle = translate('messages.toast.info_title', {}, 'Notification');
    }

    // Resolve the actual message content
    if (typeof content === 'string') {
        displayMessage = resolveValueText(content, {}, content);
    } else if (typeof content === 'object' && content !== null) {
        const titleText = content.titleKey
            ? translate(content.titleKey, content.params || {}, content.title || '')
            : resolveValueText(content.title, content.params, '');
        const messageText = content.messageKey
            ? translate(content.messageKey, content.params || {}, content.message || '')
            : resolveValueText(content.message, content.params, '');

        if (titleText && messageText) {
            displayMessage = `${titleText}: ${messageText}`;
        } else if (messageText) {
            displayMessage = messageText;
        } else if (titleText) {
            displayMessage = titleText;
        } else if (content.fallback) {
            displayMessage = content.fallback;
        } else {
            displayMessage = '';
        }
    }

    return <CustomToast t={t} type={t.type} title={displayTitle} message={displayMessage} />;
};

/**
 * Utility function to trigger the custom toast
 * @param {('success'|'error'|'info')} type - The variety of toast
 * @param {string} title - Main content (will be shown as normal text under standardized heading)
 * @param {string} [message] - Optional detail text
 */
export const notify = (type, title, message = '') => {
    const payload = { title, message, isDelete: type === 'delete' };
 
    if (type === 'success') {
        toast.success(payload);
    } else if (type === 'delete') {
        toast(payload, { duration: 4000 });
    } else if (type === 'error') {
        toast.error(payload);
    } else {
        toast(payload);
    }
};

export const notifyKey = (type, titleKey, messageKey = '', params = {}, fallback = '') => {
    const payload = { titleKey, messageKey, params, fallback, isDelete: type === 'delete' };

    if (type === 'success') {
        toast.success(payload);
    } else if (type === 'delete') {
        toast(payload, { duration: 4000 });
    } else if (type === 'error') {
        toast.error(payload);
    } else {
        toast(payload);
    }
};

export { Toaster };
